import {
  type AclOperation,
  type AclTargetType,
  type DocyrusPermission,
  type DocyrusRole,
  type DocyrusUser,
  type PermissionConfig,
  type PermissionScope
} from '../types';

/** Default always-permitted data sources (standard Docyrus system data sources) */
const DEFAULT_ALWAYS_PERMITTED: Record<string, AclOperation[]> = {
  '24ea9b34-4d74-11ef-bf16-6313ac30ce0e': [
    'view',
    'create',
    'edit',
    'delete'
  ], // report
  '95406630-4d7d-11ef-a5cf-d7d8ab9d2310': [
    'view',
    'create',
    'edit',
    'delete'
  ], // report_widget
  '996745ae-1744-11ef-a79c-abe22c3d5e97': ['view'], // tenant_html_template
  '0191e152-4619-7c08-bfc7-606f216d2757': ['view'], // tenant_action_external_request_log
  '01938bba-49ea-7b2f-8c09-adf14ab8c116': ['view'], // tenant_data_source_child_removal_rule
  'b413176e-175f-11ef-9df6-9f1f45cc8869': [
    'view',
    'create',
    'edit',
    'delete'
  ], // user_todo
  '0191b75e-79cd-7344-93f3-81a3a3f52497': [
    'view',
    'create',
    'edit',
    'delete'
  ], // user_todo_list
  '7756d784-175f-11ef-ac3b-0ba18bcdf569': [
    'view',
    'create',
    'edit',
    'delete'
  ] // user_note
};

/**
 * Operations granted globally to the `global_editor` role.
 * Derived from core_acl_operation.system_roles where target_type includes 'data_source'.
 */
const GLOBAL_EDITOR_OPERATIONS: ReadonlySet<string> = new Set([
  'view',
  'create',
  'edit',
  'delete',
  'create_bulk',
  'export',
  'import',
  'print'
]);

/**
 * Get all roles for a user (primaryRole + roles array), deduplicated by uid.
 */
export function getAllRoles(user: DocyrusUser | null | undefined): DocyrusRole[] {
  if (!user) return [];

  const roles: DocyrusRole[] = [];

  if (user.primaryRole) {
    roles.push(user.primaryRole);
  }

  if (user.roles?.length) {
    for (const role of user.roles) {
      if (!user.primaryRole || role.uid !== user.primaryRole.uid) {
        roles.push(role);
      }
    }
  }

  return roles;
}

/**
 * Check if the user has a specific role.
 *
 * - If `role` is null/undefined, returns true (no role requirement).
 * - Checks both primaryRole and roles array.
 * - Matches by slug or uid.
 */
export function hasRole(
  user: DocyrusUser | null | undefined,
  role: string | string[] | null | undefined
): boolean {
  if (role == null) return true;

  if (!user) return false;

  const allRoles = getAllRoles(user);

  if (allRoles.length === 0) return false;

  if (Array.isArray(role)) {
    if (role.length === 0) return true;

    return allRoles.some(r => role.includes(r.slug) || role.includes(r.uid));
  }

  return allRoles.some(r => r.slug === role || r.uid === role);
}

/** The id column that scopes each entity-scoped target type. */
const SCOPE_FIELD_BY_TARGET_TYPE: Partial<
  Record<AclTargetType, 'dataSourceId' | 'appId' | 'aiToolId'>
> = {
  data_source: 'dataSourceId',
  field: 'dataSourceId',
  app: 'appId',
  ai_tool: 'aiToolId'
};

/**
 * Target types with no entity scope — tenant-wide capabilities such as
 * `ai-access`, `manage_users` or `studio`.
 */
const GLOBAL_TARGET_TYPES: readonly AclTargetType[] = [
  'ai',
  'settings',
  'devtools'
];

/** Accept both the legacy positional data source id and the scope object. */
function toScope(scope?: string | null | PermissionScope): PermissionScope {
  if (scope == null) return {};

  if (typeof scope === 'string') return { dataSourceId: scope };

  return scope;
}

/**
 * Which target types a scope can be answered by.
 *
 * `field` rules record restrictions (hidden/masked/readOnly) rather than
 * grants, so they are only consulted when a caller asks for them by name.
 */
function resolveTargetTypes(scope: PermissionScope): readonly AclTargetType[] {
  if (scope.targetType) return [scope.targetType];

  if (scope.dataSourceId != null) return ['data_source'];

  if (scope.appId != null) return ['app'];

  if (scope.aiToolId != null) return ['ai_tool'];

  return GLOBAL_TARGET_TYPES;
}

/** True when a permission is scoped to the same entity the caller asked about. */
function matchesScope(permission: DocyrusPermission, scope: PermissionScope): boolean {
  const field = SCOPE_FIELD_BY_TARGET_TYPE[permission.targetType];

  // Tenant-wide target: there is no id to line up.
  if (!field) return true;

  return (permission[field] ?? null) === (scope[field] ?? null);
}

/**
 * Every operation the user is granted within a scope, merged across roles.
 *
 * Omit `scope` (or pass one with no ids) for tenant-wide operations; pass a
 * data source id, or a {@link PermissionScope} for app / AI tool targets.
 *
 * Role shortcuts are deliberately excluded — this reports what the user's rules
 * actually carry. Use {@link hasPermission} for an access decision.
 */
export function getAllowedOperations(
  user: DocyrusUser | null | undefined,
  scope?: string | null | PermissionScope
): string[] {
  if (!user) return [];

  const resolved = toScope(scope);
  const targetTypes = resolveTargetTypes(resolved);
  const operations = new Set<string>();

  if (user.permissions) {
    for (const permission of user.permissions) {
      if (!targetTypes.includes(permission.targetType)) continue;

      if (!matchesScope(permission, resolved)) continue;

      for (const op of permission.allowedOperations ?? []) {
        operations.add(op);
      }
    }

    return Array.from(operations);
  }

  /*
   * Legacy fallback for API versions that predate `permissions`. `aclRules` is
   * keyed by data source alone, so app and AI tool scopes have no equivalent
   * there and must not fall through to the null-keyed entry — that entry is a
   * merge of every entity-less rule (and of the app/ai_tool ones, whose data
   * source is also null), so it can only be read by operation slug.
   */
  if (resolved.appId != null || resolved.aiToolId != null) return [];

  const dataSourceId = resolved.dataSourceId ?? null;

  for (const rule of user.aclRules ?? []) {
    if ((rule.dataSourceId ?? null) !== dataSourceId) continue;

    for (const op of rule.allowedOperations ?? []) {
      operations.add(op);
    }
  }

  return Array.from(operations);
}

/**
 * Check if the user has permission for an operation.
 *
 * `scope` accepts a data source id, a {@link PermissionScope} for app / AI tool
 * targets, or nothing at all for tenant-wide operations such as `ai-access`,
 * `manage_users` or `studio`.
 *
 * Permission resolution order:
 * 1. super_admin role → always true
 * 2. global_editor role + dataSourceId → true if operation is in the global_editor set
 * 3. global_viewer role + dataSourceId → true only for 'view'
 * 4. Always-permitted data sources (configurable)
 * 5. The user's permissions for the scope (`permissions`, falling back to `aclRules`)
 */
export function hasPermission(
  user: DocyrusUser | null | undefined,
  operation: string,
  scope?: string | null | PermissionScope,
  config?: PermissionConfig
): boolean {
  if (!user) return false;

  // 1. super_admin gets everything
  if (hasRole(user, 'super_admin')) return true;

  const resolved = toScope(scope);
  const { dataSourceId } = resolved;

  /*
   * 2-4. The global data roles and the always-permitted list only ever grant
   * data source operations, so they stay behind a data source id.
   */
  if (dataSourceId) {
    if (hasRole(user, 'global_editor') && GLOBAL_EDITOR_OPERATIONS.has(operation)) return true;

    if (hasRole(user, 'global_viewer') && operation === 'view') return true;

    const alwaysPermitted = config?.alwaysPermittedDataSources ?? DEFAULT_ALWAYS_PERMITTED;
    const permittedOps = alwaysPermitted[dataSourceId];

    if (permittedOps?.includes(operation as AclOperation)) return true;
  }

  // 5. Rules
  return getAllowedOperations(user, resolved).includes(operation);
}
